import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';

const SignInForm = ({ onSuccess, isLoading }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Mock credentials for demo
  const mockCredentials = {
    email: 'demo@travelhub.com',
    password: 'TravelHub2024!'
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check mock credentials
    if (formData?.email !== mockCredentials?.email || formData?.password !== mockCredentials?.password) {
      setErrors({
        general: `Invalid credentials. Use: ${mockCredentials?.email} / ${mockCredentials?.password}`
      });
      return;
    }

    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General Error */}
      {errors?.general && (
        <div className="bg-error-100 border border-error text-error-600 px-4 py-3 rounded-lg text-sm">
          {errors?.general}
        </div>
      )}
      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-body-medium text-text-primary mb-2">
          Email address
        </label>
        <div className="relative">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData?.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ease-out ${
              errors?.email ? 'border-error' : 'border-border'
            }`}
            placeholder="Enter your email"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <Icon name="Mail" size={20} color="var(--color-text-secondary)" />
          </div>
        </div>
        {errors?.email && (
          <p className="mt-1 text-sm text-error">{errors?.email}</p>
        )}
      </div>
      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-body-medium text-text-primary mb-2">
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={formData?.password}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ease-out ${
              errors?.password ? 'border-error' : 'border-border'
            }`}
            placeholder="Enter your password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <Icon 
              name={showPassword ? 'EyeOff' : 'Eye'} 
              size={20} 
              color="var(--color-text-secondary)" 
            />
          </button>
        </div>
        {errors?.password && (
          <p className="mt-1 text-sm text-error">{errors?.password}</p>
        )}
      </div>
      {/* Remember Me & Forgot Password */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            id="rememberMe"
            name="rememberMe"
            type="checkbox"
            checked={formData?.rememberMe}
            onChange={handleChange}
            className="h-4 w-4 text-primary focus:ring-primary-500 border-border rounded"
          />
          <label htmlFor="rememberMe" className="ml-2 block text-sm text-text-secondary">
            Remember me
          </label>
        </div>
        <Link
          to="#"
          className="text-sm text-primary hover:underline"
        >
          Forgot password?
        </Link>
      </div>
      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-body-medium text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-out"
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            Signing in...
          </div>
        ) : (
          'Sign In'
        )}
      </button>
      {/* Demo Credentials Helper */}
      <div className="bg-secondary-100 border border-secondary-200 rounded-lg p-4">
        <h4 className="text-sm font-body-medium text-text-primary mb-2">Demo Credentials:</h4>
        <div className="text-xs text-text-secondary space-y-1">
          <p><strong>Email:</strong> {mockCredentials?.email}</p>
          <p><strong>Password:</strong> {mockCredentials?.password}</p>
        </div>
      </div>
    </form>
  );
};

export default SignInForm;